import { CalendarIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { addDays, daysBetween, isoWeekKey, localDate, TODAY_RULES } from "@zooi/domain";
import type { ReactNode } from "react";
import { ActionExecutor } from "../action-execution.tsx";
import { q } from "../api.ts";
import { FactPlaybooks } from "../fact-playbooks.tsx";
import { dateTime, day, minutes, number, usd } from "../format.ts";
import { hrefOf, metaOf, titleOf } from "../inbox-items.ts";
import { AREA_LABELS as A, INBOX_LABELS as I, KIT_LABELS as K, STATUS_LABELS as STATUS_WORD, titleText, WEEK_LABELS as W } from "../labels.ts";
import { LauncherRun } from "../local-launcher.tsx";
import { nextWeeklyRun } from "../sidebar-items.ts";
import { Callout, ErrorText, Page, Quiet, Section, Stat } from "../ui.tsx";
import { ChannelChip } from "../ui/channel.tsx";
import { InboxItemIcon } from "../ui/inbox-icon.tsx";
import { GroupHeader, ListRow } from "../ui/list.tsx";
import { CapacityMeter } from "../ui/measure.tsx";
import { Executor, PropertyList, PropertyRow } from "../ui/props.tsx";
import { AgentText } from "../ui/review.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { StatusIcon } from "../ui/status.tsx";
import { WakeupLine, WakeupNote } from "../wakeup.tsx";
import { actionStatus, actionsOfWeek, awaitingClaim, type ObservationWindow, observationWindows, plansOfWeek, planStatus, type RunRow, runRows, runState, type SetupStep, setupSteps, weekInbox } from "../week-items.ts";

// A product's week (docs/design-system.md §0.4c, redesigned 2026-10-31),
// after Linear's cycle. The main column is what needs the person, the week's
// actions and its plans; the summary column is where the runs stand, the
// person's time and the windows being observed. A past week is read-only.
// The current week opens with today's brief when the product has one (§0.4c 1a).

export function WeekPage() {
  const { productId, date } = useParams({ strict: false }) as { productId: string; date?: string };
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const week = useQuery(q.week(productId, date));
  const plans = useQuery(q.productPlans(productId));
  const actions = useQuery(q.actions(productId));
  const board = useQuery(q.campaignBoard(productId));
  const inbox = useQuery(q.inbox);
  const crumbs = [{ label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } }, { label: W.title }];
  if (week.isLoading || products.isLoading) return <Page crumbs={crumbs} wide><Skeleton /></Page>;
  if (!week.data) return <Page crumbs={crumbs}><ErrorText error={week.error} /></Page>;
  const w = week.data;
  const now = Date.now();
  const zone = w.time_zone;
  const next = w.current ? nextWeeklyRun({ weekday: w.run.weekday, hour: w.run.hour, time_zone: zone, paused_until: w.run.paused_until }, now) : null;
  const run = runState(w, next);
  const weekPlans = plansOfWeek(plans.data ?? [], w);
  const weekActions = actionsOfWeek((actions.data ?? []).filter((action) => action.status !== "live" && action.status !== "reading_due"), w);
  const needsYou = w.current ? weekInbox(inbox.data?.items ?? [], productId) : [];
  const windows = w.current ? observationWindows(actions.data ?? [], board.data?.campaigns ?? [], now) : [];
  // A list that failed to load is unknown, not empty.
  const loaded = Boolean(plans.data && actions.data && board.data && inbox.data);
  const empty = w.current && loaded && w.setup.first_plan_approved && !needsYou.length && !weekPlans.length && !weekActions.length && !windows.length;
  const length = daysBetween(w.start, w.end);
  const today = Math.min(length, daysBetween(w.start, localDate(now, zone)) + 1);
  const nav = <>
    {!w.current && <Link to="/p/$productId/week" params={{ productId }} className="z-btn sm ghost"><CalendarIcon size={16} aria-hidden />{W.thisWeek}</Link>}
    <Link to="/p/$productId/week/$date" params={{ productId, date: w.previous }} className="z-iconbtn" aria-label={W.previous} title={W.previous}><CaretLeftIcon size={16} /></Link>
    {w.next ? <Link to={w.current ? "/p/$productId/week" : "/p/$productId/week/$date"} params={{ productId, date: w.next }} className="z-iconbtn" aria-label={W.next} title={W.next}><CaretRightIcon size={16} /></Link>
      : <span className="z-iconbtn" aria-hidden data-disabled><CaretRightIcon size={16} /></span>}
  </>;
  return <Page crumbs={crumbs} wide actions={nav}
    title={w.current ? W.title : W.weekNumber(Number(isoWeekKey(w.start).slice(6)))}
    meta={<><span>{W.range(day(w.start), day(addDays(w.end, -1)))}</span><span>{w.current ? W.dayOf(today, length) : W.readOnly}</span></>}>
    <div className="z-layout-main">
      <div className="z-flow">
        {w.current && !w.setup.first_plan_approved && <Section title={W.setup.title}><SetupChecklist productId={productId} setup={w.setup} /></Section>}

        {w.current && <Today productId={productId} zone={zone} />}

        {needsYou.length > 0 && <Section title={W.needsYou} count={needsYou.length}>
          <div className="z-boxed">{needsYou.map((item) => <ListRow key={item.key} to={hrefOf(item)} icon={<InboxItemIcon item={item} />} title={titleOf(item)}
            sub={I.reason[item.reason.code] ?? I.kind[item.kind]} meta={metaOf(item)} />)}</div>
        </Section>}

        {weekActions.length > 0 && <Section title={w.current ? W.actions : W.pastActions} count={weekActions.length}>
          <div className="z-boxed">{groupByStatus(weekActions).map(([kind, rows]) => <Group key={kind} kind={kind} count={rows.length}>
            {rows.map((action) => <ListRow key={action.id} to={`/p/${productId}/actions/${action.id}`} status={actionStatus(action)} title={titleText(action.title)}
              executor={<ActionExecutor action={action} />}
              chips={<ChannelChip channel={action.channel} />} meta={action.committed_at ? dateTime(action.committed_at, zone) : undefined} />)}
          </Group>)}</div>
        </Section>}

        <Section title={w.current ? W.plans : W.pastPlans} count={weekPlans.length || undefined}>
          {plans.error ? <ErrorText error={plans.error} />
            : !plans.data ? <Skeleton shape="list" rows={1} />
            : weekPlans.length ? <div className="z-boxed z-week-plans">{weekPlans.map((plan) => <PlanRow key={plan.id} productId={productId} plan={plan} zone={zone} />)}</div>
            : <p className="z-empty">{w.current ? (run.kind === "scheduled" ? `${W.waitingPlan} · ${W.run.scheduled(dateTime(run.at, zone))}` : W.waitingPlan) : W.noPlan}</p>}
        </Section>

        {empty && <Quiet title={W.quietTitle}><p>{W.quietNote}</p></Quiet>}
        <ErrorText error={actions.error ?? board.error ?? inbox.error} />
      </div>
      <aside className="z-detail-summary is-stacked z-week-summary" aria-label={W.summary}>
        <Runs productId={productId} week={w} rows={runRows(w.run.requests, now)} next={next} unclaimed={w.current ? awaitingClaim(actions.data ?? []).length : 0} />
        {w.capacity && (w.capacity.weekly_minutes
          ? <div><CapacityMeter used={w.capacity.used_minutes} total={w.capacity.weekly_minutes} /><div className="z-capacity-note"><Link className="z-link" to="/settings/products/$productId" params={{ productId }}>{W.capacityAdjust}</Link></div></div>
          : <div className="z-capacity"><div className="z-capacity-head"><span className="z-muted">{K.capacity}</span><Link className="z-link" to="/settings/products/$productId" params={{ productId }}>{W.capacitySet}</Link></div></div>)}
        {w.agent_capacity && <div>
          <CapacityMeter label={W.agentTime} used={w.agent_capacity.used_minutes} total={w.agent_capacity.weekly_minutes} />
          <div className="z-capacity-note">{W.agentCostUsed(usd(w.agent_capacity.used_usd), w.agent_capacity.weekly_usd === null ? "–" : usd(w.agent_capacity.weekly_usd))}{w.agent_capacity.unknown_cost_runs > 0 ? ` ${W.agentCostUnknown(w.agent_capacity.unknown_cost_runs)}` : ""}</div>
        </div>}
        {windows.length > 0 && <Windows productId={productId} windows={windows} />}
      </aside>
    </div>
  </Page>;
}

const T = W.today;

/** Today's brief: zooi's numbers, and the agent's note under them. What could not be read shows as "–". */
function Today({ productId, zone }: { productId: string; zone: string }) {
  const { data } = useQuery(q.todayBrief(productId));
  const today = data?.today;
  if (!today) return null;
  const posts = today.posts;
  return <Section title={T.title} note={today.x_handle ? (today.read_at ? T.read(dateTime(today.read_at, zone)) : T.notRead) : undefined}>
    <div className="z-flow">
      <div className="z-stats">
        <Stat label={T.followers} value={number(today.followers)} note={today.followers_change === null ? undefined : T.change(today.followers_change)} />
        <Stat label={T.replies} value={number(today.replies.posted)} note={T.queued(today.replies.queued)} />
        <Stat label={T.views(posts?.hours ?? TODAY_RULES.posts_hours)} value={number(posts?.views)} />
      </div>
      {posts && posts.kinds.length > 0 && <div className="z-scroll-x"><table className="z-table">
        <thead><tr><th>{T.type}</th><th className="is-num">{T.count}</th><th className="is-num">{T.postViews}</th><th className="is-num">{T.average}</th></tr></thead>
        <tbody>{posts.kinds.map((row) => <tr key={row.kind}><td>{T.kinds[row.kind]}</td><td className="is-num">{number(row.count)}</td><td className="is-num">{number(row.views)}</td><td className="is-num">{number(row.average)}</td></tr>)}</tbody>
      </table></div>}
      {posts?.best && <TodayPost label={T.best} post={posts.best} />}
      {posts?.worst && <TodayPost label={T.worst} post={posts.worst} />}
      {today.problems.map((problem) => <Callout key={problem.code} tone="warn">
        {problem.code === "reply_missing" ? T.replyMissing(problem.count) : <>{T.xFailed} <Link className="z-link" to="/p/$productId/data" params={{ productId }} search={{ view: "sources" }}>{T.checkConnection}</Link></>}
      </Callout>)}
      {today.note && <AgentText name="Agent" label={T.noteBy} lines={4}>{today.note.text}</AgentText>}
    </div>
  </Section>;
}

function TodayPost({ label, post }: { label: string; post: NonNullable<NonNullable<C.TodayBrief["posts"]>["best"]> }) {
  return <div className="z-today-post">
    <span className="z-muted">{label}</span>
    {post.text ? <blockquote className="z-reply-quote z-agent-text">{post.text.text}</blockquote> : <span aria-hidden />}
    <a className="z-link" href={post.url} target="_blank" rel="noreferrer">{T.viewCount(post.views)}</a>
  </div>;
}

const RUN_TEXT: Record<RunRow["state"], (at: string) => string> = {
  scheduled: W.run.booked, waiting: W.run.waiting, dispatched: W.run.dispatched, running: W.run.running, reported: W.run.reported, missed: () => W.run.missed,
};

/**
 * Where the week's runs stand, one row per request, then the next weekly
 * run and any approved work no agent has claimed. A run handles all of it,
 * so the run entry point is the only button here.
 */
function Runs({ productId, week, rows, next, unclaimed }: { productId: string; week: C.WeekView; rows: RunRow[]; next: string | null; unclaimed: number }) {
  const zone = week.time_zone;
  const running = rows.some((row) => row.state === "running");
  return <div className="z-flow">
    <h2>{W.runs}</h2>
    {(rows.length > 0 || week.current) && <PropertyList>
      {rows.map((row) => <PropertyRow key={row.id} icon={<StatusIcon kind={row.status} label="" />} label={W.runKind[row.kind] ?? row.kind}>{RUN_TEXT[row.state](dateTime(row.at, zone))}</PropertyRow>)}
      {week.current && <PropertyRow label={W.nextRun}>{week.run.paused_until ? W.run.paused(day(week.run.paused_until)) : next ? dateTime(next, zone) : undefined}</PropertyRow>}
      {unclaimed > 0 && <PropertyRow label={W.awaitingClaim}>{W.actionCount(unclaimed)}</PropertyRow>}
      {week.current && <WakeupLine wakeups={week.wakeups} />}
    </PropertyList>}
    {week.current && <WakeupNote wakeups={week.wakeups} productId={productId} />}
    {!rows.length && !week.current && <p className="z-muted">{W.run.none}</p>}
    {week.current && !running && <LauncherRun productId={productId} />}
  </div>;
}

/** Open observation windows: how far each has come, never an early number (vision §8.2). */
function Windows({ productId, windows }: { productId: string; windows: ObservationWindow[] }) {
  return <div className="z-flow">
    <h2>{W.observing}<span className="z-count">{windows.length}</span></h2>
    <ul className="z-week-windows">{windows.map((window) => <li key={window.key}>
      <Link className="z-week-window" to={window.kind === "action" ? "/p/$productId/actions/$actionId" : "/p/$productId/campaigns/$campaignId"} params={{ productId, actionId: window.id, campaignId: window.id }}>
        <StatusIcon kind={window.status} progress={window.day && window.days ? window.day / window.days : null} label="" />
        <span className="z-week-window-title">{typeof window.title === "string" ? window.title : titleText(window.title)}</span>
        <span className="z-week-window-day">{window.day && window.days ? W.dayOf(window.day, window.days) : "–"}</span>
      </Link>
    </li>)}</ul>
  </div>;
}

function SetupChecklist({ productId, setup }: { productId: string; setup: C.WeekView["setup"] }) {
  const account = setup.search_queries !== null;
  const steps: Record<SetupStep, { title: string; note: string; link: ReactNode }> = {
    agent: { title: W.setup.agent, note: W.setup.agentNote, link: <Link to="/settings/products/$productId/agent" params={{ productId }} className="z-btn sm">{W.setup.agentAction}</Link> },
    facts: { title: account ? W.setup.factsAccount : W.setup.facts, note: account ? W.setup.factsAccountNote : W.setup.factsNote, link: <Link to="/p/$productId/profile" params={{ productId }} className="z-btn sm">{account ? W.setup.factsAccountAction : W.setup.factsAction}</Link> },
    queries: { title: W.setup.queries, note: W.setup.queriesNote, link: <Link to="/p/$productId/data" params={{ productId }} search={{ view: "sources" }} hash="search-queries" className="z-btn sm">{W.setup.queriesAction}</Link> },
    wakeup: { title: W.setup.wakeup, note: W.setup.wakeupNote, link: <Link to="/settings/products/$productId/agent" params={{ productId }} hash="wakeup" className="z-btn sm">{W.setup.wakeupAction}</Link> },
    run: { title: W.setup.run, note: account ? W.setup.runNoteAccount : W.setup.runNote, link: <Link to="/settings/products/$productId/agent" params={{ productId }} hash="run-settings" className="z-btn sm">{W.runSettings}</Link> },
    plan: { title: W.setup.plan, note: W.setup.planNote, link: <Link to="/inbox" className="z-btn sm">{W.setup.planAction}</Link> },
  };
  return <div className="z-flow">
    <div className="z-boxed">{setupSteps(setup).map(({ step, done }) => <ListRow key={step} status={done ? "done" : "not_started"} title={steps[step].title} sub={steps[step].note} end={done ? undefined : steps[step].link} />)}</div>
    {!setup.facts_confirmed && <FactPlaybooks productId={productId} account={account} />}
  </div>;
}

/** A plan in two lines: which plan and where it stands, then the agent's summary. Its full reasoning is on the review page. */
function PlanRow({ productId, plan, zone }: { productId: string; plan: C.AgentPlan; zone: string }) {
  const state = plan.body.nothing_this_week ? W.nothingThisWeek
    : `${I.planStatus[plan.status] ?? plan.status} · ${W.planActions(plan.body.actions.length, minutes(plan.checked.capacity.requested_minutes))}`;
  return <ListRow to={`/p/${productId}/plans/${plan.id}`} status={planStatus(plan.status)}
    title={plan.scope === "addendum" ? W.addendum : W.weeklyPlan} sub={plan.body.summary}
    chips={<span className="z-week-plan-state">{state}</span>}
    executor={<Executor engine={plan.engine.name} name={I.engine[plan.engine.name] ?? plan.engine.name} />}
    meta={dateTime(plan.created_at, zone)} />;
}

function Group({ kind, count, children }: { kind: ReturnType<typeof actionStatus>; count: number; children: ReactNode }) {
  return <><GroupHeader label={STATUS_WORD[kind] ?? kind} count={count} status={kind} />{children}</>;
}

function groupByStatus(actions: C.Action[]): [ReturnType<typeof actionStatus>, C.Action[]][] {
  const groups = new Map<ReturnType<typeof actionStatus>, C.Action[]>();
  for (const action of actions) {
    const kind = actionStatus(action);
    groups.set(kind, [...(groups.get(kind) ?? []), action]);
  }
  return [...groups.entries()];
}
